import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, Eye, EyeOff, Loader2, Clock } from 'lucide-react';
import { getPlatformInfo } from './platforms/index';
import type { ExternalPlatformConfig } from '@/hooks/useConnectorManagement';
import type { SettingspageTranslations } from '@/hooks/useSettingspageTranslations';

// Form schema factory with translations
const createPlatformConfigSchema = (i18n: any) =>
  z.object({
    platform: z.string().min(1, i18n.api.validation.platformRequired),
    name: z
      .string()
      .min(1, i18n.api.validation.configurationNameRequired)
      .max(100, i18n.api.validation.nameMaxLength),
    settings: z.record(z.any()).refine(settings => {
      return Object.keys(settings).length > 0;
    }, i18n.api.validation.platformSettingsRequired),
  });

type PlatformConfigFormData = z.infer<ReturnType<typeof createPlatformConfigSchema>>;

interface ConnectorFormProps {
  editingConfig?: ExternalPlatformConfig | null;
  selectedPlatform: string;
  setSelectedPlatform: (platform: string) => void;
  onSubmit: (data: PlatformConfigFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  attributes: any[];
  priceTypes: any[];
  showSecrets: Record<string, boolean>;
  toggleSecretVisibility: (configId: number, fieldKey: string) => void;
  i18n: SettingspageTranslations;
}

export default function ConnectorForm({
  editingConfig,
  selectedPlatform,
  setSelectedPlatform,
  onSubmit,
  onCancel,
  isLoading = false,
  attributes,
  priceTypes,
  showSecrets,
  toggleSecretVisibility,
  i18n,
}: ConnectorFormProps) {
  // Generate platforms with translations
  const platforms = i18n.api.platforms.list;

  const form = useForm<PlatformConfigFormData>({
    resolver: zodResolver(createPlatformConfigSchema(i18n)),
    defaultValues: {
      platform: editingConfig?.platform || selectedPlatform,
      name: editingConfig?.name || '',
      settings: editingConfig?.settings || {},
    },
  });

  // Get platform info from translated platforms
  const platformInfo = platforms.find(p => p.value === selectedPlatform);

  // Effect to populate form when editing existing config
  React.useEffect(() => {
    if (editingConfig) {
      // For editing, populate with existing data
      form.reset({
        platform: editingConfig.platform,
        name: editingConfig.name,
        settings: editingConfig.settings,
      });
    } else {
      // For new config, reset to empty
      form.reset({
        platform: selectedPlatform,
        name: '',
        settings: {},
      });
    }
  }, [editingConfig, form, selectedPlatform]);

  // Handle platform selection
  const handlePlatformSelect = (platform: string) => {
    setSelectedPlatform(platform);
    form.setValue('platform', platform);
    const newPlatformInfo = getPlatformInfo(platform);
    if (newPlatformInfo) {
      // Initialize empty settings for the platform
      const initialSettings: Record<string, any> = {};
      newPlatformInfo.fields.forEach(field => {
        initialSettings[field.key] = '';
      });

      // Initialize mapping structure based on platform
      if (platform === 'shopify') {
        initialSettings.mapping = {
          price_type_map: { retail: '', compare_at: '' },
          inventory: { quantity_attribute: '', management_attribute: '' },
          variants: { option1_attribute: '', option2_attribute: '', option3_attribute: '' },
          seo: { title_attribute: '', description_attribute: '' },
        };
      } else if (platform === 'magento') {
        initialSettings.mapping = {
          price_type_map: { base: '', special: '' },
          inventory: { quantity_attribute: '', manage_stock_attribute: '' },
        };
      } else if (platform === 'bigcommerce') {
        initialSettings.mapping = {
          price_type_map: { base: '', sale: '', retail: '' },
          inventory: { quantity_attribute: '', track_attribute: '' },
          shipping: { width_attribute: '', height_attribute: '', depth_attribute: '' },
        };
      }

      form.setValue('settings', initialSettings);
    }
  };

  // Render platform selection
  const renderPlatformSelection = () => (
    <div className="space-y-4">
      <div>
        <Label>{i18n.api.modal.configuration.platform.label}</Label>
        <Select value={selectedPlatform} onValueChange={handlePlatformSelect}>
          <SelectTrigger>
            <SelectValue placeholder={i18n.api.modal.platformSelection.placeholder} />
          </SelectTrigger>
          <SelectContent>
            {platforms.map(platform => (
              <SelectItem key={platform.value} value={platform.value}>
                <div className="flex items-center gap-2">
                  <span>{platform.icon}</span>
                  <span>{platform.label}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {platformInfo && (
        <div className="rounded-lg border bg-muted/20 p-4">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="font-medium">{platformInfo.label}</h4>
              <p className="mt-1 text-sm text-muted-foreground">{platformInfo.description}</p>
              <div className="mt-2 flex flex-wrap gap-1">
                {platformInfo.supportedFeatures.map(feature => (
                  <Badge key={feature} variant="outline" className="text-xs">
                    {feature}
                  </Badge>
                ))}
              </div>
            </div>
            {platformInfo.apiDocs && (
              <Button variant="ghost" size="sm" asChild>
                <a href={platformInfo.apiDocs} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="size-4" />
                </a>
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // Render configuration name
  const renderConfigName = () => (
    <div className="space-y-2">
      <Label htmlFor="name">{i18n.api.modal.configuration.name.label}</Label>
      <Input
        id="name"
        {...form.register('name')}
        placeholder={i18n.api.modal.configuration.name.placeholder}
      />
      {form.formState.errors.name && (
        <p className="text-sm text-red-600">{form.formState.errors.name.message}</p>
      )}
    </div>
  );

  // Render platform fields
  const renderPlatformFields = () => {
    if (!platformInfo) return null;

    return (
      <div className="space-y-4">
        <h4 className="font-medium">{i18n.api.modal.configuration.settings.title}</h4>
        <div className="grid gap-4">
          {platformInfo.fields.map(field => {
            const fieldKey = `settings.${field.key}`;
            const isSecret = field.type === 'password';
            const secretKey = editingConfig
              ? `${editingConfig.id}-${field.key}`
              : `new-${field.key}`;
            const showSecret = showSecrets[secretKey];

            return (
              <div key={field.key} className="space-y-2">
                <Label htmlFor={field.key}>
                  {field.label}
                  {field.required && <span className="ml-1 text-red-500">*</span>}
                </Label>
                {field.description && (
                  <p className="text-xs text-muted-foreground">{field.description}</p>
                )}

                {field.type === 'select' ? (
                  <Select
                    value={form.watch(fieldKey as any) || undefined}
                    onValueChange={value => form.setValue(fieldKey as any, value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={field.placeholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {field.options?.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="relative">
                    <Input
                      id={field.key}
                      type={isSecret && !showSecret ? 'password' : 'text'}
                      {...form.register(fieldKey as any)}
                      placeholder={field.placeholder}
                    />
                    {isSecret && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3"
                        onClick={() => toggleSecretVisibility(editingConfig?.id || 0, field.key)}
                      >
                        {showSecret ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render mapping configuration
  const renderMappingConfiguration = () => {
    if (!platformInfo?.mappingComponent) return null;

    const MappingComponent = platformInfo.mappingComponent;

    return (
      <div className="space-y-4">
        <h4 className="font-medium">{i18n.api.modal.configuration.mapping.title}</h4>
        <MappingComponent
          form={form}
          attributes={attributes}
          priceTypes={priceTypes}
          selectedPlatform={selectedPlatform}
          i18n={i18n}
          configId={editingConfig?.id}
        />
      </div>
    );
  };

  // Render scheduling configuration
  const renderSchedulingConfiguration = () => {
    const autoSyncEnabled = form.watch('settings.auto_sync_enabled') || false;
    const syncInterval = form.watch('settings.sync_interval') || 'disabled';

    return (
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="auto_sync_enabled"
            checked={autoSyncEnabled}
            onChange={e => {
              form.setValue('settings.auto_sync_enabled', e.target.checked);
              if (!e.target.checked) {
                form.setValue('settings.sync_interval', 'disabled');
              } else {
                form.setValue('settings.sync_interval', 'daily');
              }
            }}
            className="rounded border-gray-300"
          />
          <Label htmlFor="auto_sync_enabled" className="text-sm font-medium">
            {i18n.api.modal.configuration.scheduling.enableLabel}
          </Label>
        </div>

        {autoSyncEnabled && (
          <div className="space-y-2">
            <Label>{i18n.api.modal.configuration.scheduling.intervalLabel}</Label>
            <Select
              value={syncInterval}
              onValueChange={value => form.setValue('settings.sync_interval', value)}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={i18n.api.modal.configuration.scheduling.intervalPlaceholder}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hourly">
                  <div className="flex items-center gap-2">
                    <Clock className="size-4" />
                    <div>
                      <div>{i18n.api.modal.configuration.scheduling.intervals.hourly.label}</div>
                      <div className="text-xs text-muted-foreground">
                        {i18n.api.modal.configuration.scheduling.intervals.hourly.description}
                      </div>
                    </div>
                  </div>
                </SelectItem>
                <SelectItem value="daily">
                  <div className="flex items-center gap-2">
                    <Clock className="size-4" />
                    <div>
                      <div>{i18n.api.modal.configuration.scheduling.intervals.daily.label}</div>
                      <div className="text-xs text-muted-foreground">
                        {i18n.api.modal.configuration.scheduling.intervals.daily.description}
                      </div>
                    </div>
                  </div>
                </SelectItem>
                <SelectItem value="weekly">
                  <div className="flex items-center gap-2">
                    <Clock className="size-4" />
                    <div>
                      <div>{i18n.api.modal.configuration.scheduling.intervals.weekly.label}</div>
                      <div className="text-xs text-muted-foreground">
                        {i18n.api.modal.configuration.scheduling.intervals.weekly.description}
                      </div>
                    </div>
                  </div>
                </SelectItem>
                <SelectItem value="monthly">
                  <div className="flex items-center gap-2">
                    <Clock className="size-4" />
                    <div>
                      <div>{i18n.api.modal.configuration.scheduling.intervals.monthly.label}</div>
                      <div className="text-xs text-muted-foreground">
                        {i18n.api.modal.configuration.scheduling.intervals.monthly.description}
                      </div>
                    </div>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {i18n.api.modal.configuration.scheduling.note}
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
      <Tabs defaultValue="basic" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="basic">{i18n.api.modal.configuration.tabs.basic}</TabsTrigger>
          <TabsTrigger value="mapping" disabled={!selectedPlatform}>
            {i18n.api.modal.configuration.tabs.advanced}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{i18n.api.modal.configuration.cards.platformSelection}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {renderPlatformSelection()}
              {renderConfigName()}
            </CardContent>
          </Card>

          {selectedPlatform && (
            <Card>
              <CardHeader>
                <CardTitle>{i18n.api.modal.configuration.settings.title}</CardTitle>
              </CardHeader>
              <CardContent>{renderPlatformFields()}</CardContent>
            </Card>
          )}

          {selectedPlatform && (
            <Card>
              <CardHeader>
                <CardTitle>{i18n.api.modal.configuration.scheduling.title}</CardTitle>
                <p className="text-sm text-muted-foreground">
                  {i18n.api.modal.configuration.scheduling.description}
                </p>
              </CardHeader>
              <CardContent>{renderSchedulingConfiguration()}</CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="mapping" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{i18n.api.modal.configuration.mapping.title}</CardTitle>
            </CardHeader>
            <CardContent>{renderMappingConfiguration()}</CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={onCancel}>
          {i18n.api.modal.configuration.actions.cancel}
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading && <Loader2 className="mr-2 size-4 animate-spin" />}
          {isLoading
            ? i18n.api.modal.configuration.actions.saving
            : i18n.api.modal.configuration.actions.save}
        </Button>
      </div>
    </form>
  );
}
